import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../core/hooks/use-language';
import { useTranslation } from '../../core/hooks/use-translation';
import type { TranslationKey } from '../../core/i18n/translation';
import { TextLines } from './text-lines';

interface FaqEntry {
    readonly question: TranslationKey;
    readonly answer: TranslationKey;
}

const ENTRIES: readonly FaqEntry[] = [
    { question: 'site.faq.q1', answer: 'site.faq.a1' },
    { question: 'site.faq.q2', answer: 'site.faq.a2' },
    { question: 'site.faq.q3', answer: 'site.faq.a3' },
    { question: 'site.faq.q4', answer: 'site.faq.a4' },
    { question: 'site.faq.q5', answer: 'site.faq.a5' },
    { question: 'site.faq.q6', answer: 'site.faq.a6' }
];

/**
 * The questions, one open at a time and the first open to begin with — the live site's behaviour,
 * which also means the section never renders as six identical closed bars with nothing to read.
 *
 * The answers stay in the DOM when closed, as they do on the live page: the panel is collapsed by
 * height, so the text is there for a crawler and for a browser's find-in-page.
 */
export function FaqSection() {
    const { t } = useTranslation();
    const language = useLanguage();
    const [open, setOpen] = useState<TranslationKey | null>(ENTRIES[0].question);

    return (
        <section className="section section-alt faq-section" id="faq">
            <div className="container">
                <div>
                    <span className="section-label">{t('site.faq.label')}</span>
                    <h2 className="section-title">{t('site.faq.title')}</h2>
                    <div className="faq-list">
                        {ENTRIES.map((entry) => {
                            const isOpen = entry.question === open;

                            return (
                                <div
                                    className={isOpen ? 'faq-item open' : 'faq-item'}
                                    key={entry.question}
                                >
                                    <button
                                        type="button"
                                        className="faq-question"
                                        aria-expanded={isOpen}
                                        onClick={() => setOpen(isOpen ? null : entry.question)}
                                    >
                                        <span>{t(entry.question)}</span>
                                        <div className="faq-chevron">
                                            <svg viewBox="0 0 24 24">
                                                <polyline points="6 9 12 15 18 9" />
                                            </svg>
                                        </div>
                                    </button>
                                    <div className="faq-answer">
                                        <div className="faq-answer-inner">
                                            <TextLines text={t(entry.answer)} />
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/*
                        The second way in to the posts, after the one beside the hero's call to
                        action. The hero's is there to be found; this one is there to be wanted: it
                        is the point on the page where somebody is reading answers and can run out
                        of them, which is when a longer read is what they want next. Outlined
                        rather than filled, so it stays quieter than the booking button that is
                        still the page's one action.
                    */}
                    <div className="faq-more">
                        <p className="faq-more-text">{t('site.faq.more_text')}</p>
                        <Link to={`/${language}/blog`} className="btn btn-outline-brand btn-sm">
                            {t('site.faq.more_action')}
                            <ArrowIcon />
                        </Link>
                    </div>
                </div>
            </div>
        </section>
    );
}

/** `aria-hidden` because it sits beside its own label; announcing it would say the word twice. */
function ArrowIcon() {
    return (
        <svg
            className="faq-more-icon"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
            focusable="false"
        >
            <path d="M5 12h14M13 6l6 6-6 6" />
        </svg>
    );
}
